import { Badge } from "@/components/Badge";
import { Pill, StatPill, LabelPill } from "@/components/StatPill";
import { BarChart } from "@/components/BarChart";
import { TrendingDown, Mail, Calendar, Sparkles, BarChart3 } from "lucide-react";

export const DecemberHeroSection = () => {
  return (
    <section className="hero-card">
      <div className="relative z-10">
        {/* Badge */}
        <div className="animate-fade-up" style={{ animationDelay: "100ms" }}>
          <Badge>December Email Performance Report</Badge>
        </div>
        
        {/* Headline */}
        <div className="mt-6 sm:mt-8 animate-fade-up" style={{ animationDelay: "200ms" }}>
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-5 h-5 text-primary" />
            <span className="text-sm font-medium text-primary">Industry Research Summary</span>
          </div>
          <h1 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight leading-[1.1] text-foreground">
            Why December Email
            <br />
            <span className="text-primary">Replies Drop</span>
          </h1>
          <p className="font-display text-xl sm:text-2xl text-muted-foreground mt-3 font-medium">
            Seasonality versus Deliverability
          </p>
        </div>
        
        {/* Subtitle */}
        <p className="text-muted-foreground text-base sm:text-lg leading-relaxed max-w-[700px] mt-5 animate-fade-up" style={{ animationDelay: "300ms" }}>
          Independent benchmark data shows a clear pattern: December performance drops 
          are caused by holiday timing, inbox overload, and user behavior,
          <span className="font-semibold text-foreground"> not technical deliverability failure</span>.
        </p>

        {/* Quick Stats Row - Verified data */}
        <div className="flex flex-wrap gap-4 sm:gap-6 mt-8 animate-fade-up" style={{ animationDelay: "400ms" }}>
          <div className="flex items-center gap-2.5 text-sm sm:text-base text-muted-foreground bg-muted/50 rounded-full px-4 py-2">
            <Mail className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
            <span><strong className="text-foreground">84%</strong> global inbox rate</span>
          </div>
          <div className="flex items-center gap-2.5 text-sm sm:text-base text-muted-foreground bg-muted/50 rounded-full px-4 py-2">
            <TrendingDown className="w-4 h-4 sm:w-5 sm:h-5 text-destructive" />
            <span><strong className="text-foreground">6.1% → 2.9%</strong> conversion</span>
          </div>
          <div className="flex items-center gap-2.5 text-sm sm:text-base text-muted-foreground bg-muted/50 rounded-full px-4 py-2">
            <BarChart3 className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
            <span><strong className="text-foreground">+30%</strong> email volume</span>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-[1.1fr_1fr] gap-6 sm:gap-8 mt-10 animate-fade-up" style={{ animationDelay: "500ms" }}>
          {/* Left column */}
          <div className="space-y-5">
            <Pill accent="2024-2025 Data">
              Based on Validity, GetResponse & Mailjet research
            </Pill>
            
            <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
              During the holiday season, email volume surges 30-32% while subscriber attention 
              becomes highly selective. Even well-configured campaigns see lower responses 
              as inboxes overflow with promotional content.
            </p>
            
            <div className="grid grid-cols-2 gap-3">
              <StatPill value="87.2%" label="Gmail inbox rate" />
              <StatPill value="75.6%" label="Microsoft inbox" />
              <StatPill value="0.22%" label="unsubscribe rate" />
              <StatPill value="13-15%" label="avg open rate" />
            </div>
          </div>

          {/* Right column - Chart card with verified data */}
          <div className="accent-card">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between text-sm text-muted-foreground mb-4 gap-1">
              <span className="font-semibold text-foreground">Conversion Rate Drop</span>
              <span className="pill text-[11px] py-0.5">Smart Insights 2025</span>
            </div>

            <div className="space-y-4">
              <div>
                <LabelPill label="November (Black Friday)" value="6.1%" />
                <BarChart percentage={100} className="mt-1.5" />
              </div>

              <div>
                <LabelPill label="December average" value="2.9-3.1%" />
                <BarChart percentage={48} variant="red" className="mt-1.5" />
              </div>

              <p className="text-[11px] sm:text-xs text-muted-foreground pt-3 border-t border-border">
                Source: Smart Insights & Dynamic Yield e-commerce conversion benchmarks
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
